import { isoDateTime, z } from "../zod.js";
import { ActiveStatus, Currency, Uuid } from "../identity/admin-schemas.js";

// Module 01 — Catalogue: the full P1 contract (spec 01 §2 data model, §5 API).
// The PR #8 walking skeleton shipped products (draft create + list + detail) and
// the uom picker feed; this file adds the rest of §2/§5 — uom + conversion CRUD,
// attribute schemas, product-specific conversions, BOMs (lines/outputs/
// substitutes/effective), routings (steps/effective) + step bindings, standard
// cost runs + items with §2.16 field-level redaction, templates, the floor
// delta cache, and the ECO change log. Cost fields are `.optional()`: the
// serialization layer omits them entirely for principals without
// `catalogue.cost.read` (spec 01 §7 field-level redaction).

// --- shared scalars ---

/** ISO-8601 business date (tenant timezone), e.g. effective_from. */
const BusinessDate = z.string().openapi({ format: "date", example: "2026-08-03" });
/** Integer minor units (money), e.g. tiyn. Never a float (the money convention). */
const MinorMoney = z.number().int();

// --- units of measure (spec 01 §2.1, §2.2) ---

export const UomDimension = z.enum([
  "length",
  "mass",
  "count",
  "area",
  "volume",
  "time",
  "container",
]);

export const UomSource = z.enum(["vertical_pack", "custom"]);

export const Uom = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    dimension: UomDimension,
    is_base: z.boolean(),
    decimal_precision: z.number().int().min(0).max(6),
    source: UomSource,
    status: ActiveStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("Uom");

export const UomListResponse = z.object({ items: z.array(Uom) }).openapi("UomListResponse");

export const UomCreateRequest = z
  .object({
    code: z.string().min(1).max(16),
    name: z.string().min(1).max(200),
    dimension: UomDimension,
    is_base: z.boolean().optional(),
    decimal_precision: z.number().int().min(0).max(6).optional(),
  })
  .openapi("UomCreateRequest");

export const UomPatchRequest = z
  .object({
    name: z.string().min(1).max(200).optional(),
    decimal_precision: z.number().int().min(0).max(6).optional(),
    status: ActiveStatus.optional(),
  })
  .openapi("UomPatchRequest");

export const UomConversion = z
  .object({
    id: Uuid,
    from_uom_id: Uuid,
    to_uom_id: Uuid,
    factor: z.number().positive(),
  })
  .openapi("UomConversion");

export const UomConversionCreateRequest = z
  .object({
    from_uom_id: Uuid,
    to_uom_id: Uuid,
    factor: z.number().positive(),
  })
  .openapi("UomConversionCreateRequest");

export const UomConversionListResponse = z
  .object({ items: z.array(UomConversion) })
  .openapi("UomConversionListResponse");

// --- attribute schemas (spec 01 §2.3) ---

export const AttributeSchema = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    version: z.number().int().min(1),
    json_schema: z.record(z.string(), z.unknown()),
    ui_hints: z.record(z.string(), z.unknown()),
    source: UomSource,
    status: ActiveStatus,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("AttributeSchema");

export const AttributeSchemaCreateRequest = z
  .object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    json_schema: z.record(z.string(), z.unknown()),
    ui_hints: z.record(z.string(), z.unknown()).optional(),
  })
  .openapi("AttributeSchemaCreateRequest");

export const AttributeSchemaPatchRequest = z
  .object({
    name: z.string().min(1).max(200).optional(),
    ui_hints: z.record(z.string(), z.unknown()).optional(),
    status: ActiveStatus.optional(),
  })
  .openapi("AttributeSchemaPatchRequest");

export const AttributeSchemaListResponse = z
  .object({ items: z.array(AttributeSchema) })
  .openapi("AttributeSchemaListResponse");

// --- products (spec 01 §2.4) ---

export const ProductKind = z.enum([
  "finished_good",
  "semi_finished",
  "raw_material",
  "consumable",
  "service",
]);
export const MakeOrBuy = z.enum(["make", "buy", "both"]);
export const ProductTracking = z.enum(["none", "lot", "serial", "measured_unit"]);
export const ProductStatus = z.enum(["draft", "active", "discontinued"]);

/**
 * The product as returned by the API (spec 01 §2.4). `standard_cost_minor` and
 * `cost_run_id` are `.optional()`: the serialization layer omits them entirely
 * for principals without `catalogue.cost.read` (spec 01 §7 field-level
 * redaction) — an absent field, never a null placeholder, so no cost signal
 * leaks. All other §2.4 columns are always present.
 */
export const Product = z
  .object({
    id: Uuid,
    sku: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    kind: ProductKind,
    make_or_buy: MakeOrBuy,
    attribute_schema_id: Uuid.nullable(),
    attributes: z.record(z.string(), z.unknown()),
    base_uom_id: Uuid,
    tracking: ProductTracking,
    assembly_scrap_pct: z.number(),
    purchase_lead_days: z.number().int().nullable(),
    manufacture_lead_days: z.number().int().nullable(),
    default_facility_id: Uuid.nullable(),
    price_unit_qty: z.number().positive(),
    currency: Currency,
    qr_code_value: z.string().nullable(),
    status: ProductStatus,
    // Cost fields — redacted (omitted) without catalogue.cost.read (§7).
    standard_cost_minor: MinorMoney.nullable().optional(),
    cost_run_id: Uuid.nullable().optional(),
    created_by: Uuid,
    updated_by: Uuid,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("Product");

export const ProductCreateRequest = z
  .object({
    sku: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    kind: ProductKind,
    base_uom_id: Uuid,
    attribute_schema_id: Uuid.nullable().optional(),
    attributes: z.record(z.string(), z.unknown()).optional(),
    description: z.string().max(2000).nullable().optional(),
    template_code: z.string().min(1).max(64).optional(),
    tracking: ProductTracking.optional(),
    make_or_buy: MakeOrBuy.optional(),
    assembly_scrap_pct: z.number().min(0).lt(100).optional(),
    purchase_lead_days: z.number().int().min(0).nullable().optional(),
    manufacture_lead_days: z.number().int().min(0).nullable().optional(),
    default_facility_id: Uuid.nullable().optional(),
    price_unit_qty: z.number().positive().optional(),
  })
  .openapi("ProductCreateRequest");

export const ProductPatchRequest = z
  .object({
    // sku is immutable once active (spec 01 §5) — service-enforced.
    sku: z.string().min(1).max(64).optional(),
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    attribute_schema_id: Uuid.nullable().optional(),
    attributes: z.record(z.string(), z.unknown()).optional(),
    tracking: ProductTracking.optional(),
    make_or_buy: MakeOrBuy.optional(),
    assembly_scrap_pct: z.number().min(0).lt(100).optional(),
    purchase_lead_days: z.number().int().min(0).nullable().optional(),
    manufacture_lead_days: z.number().int().min(0).nullable().optional(),
    default_facility_id: Uuid.nullable().optional(),
    price_unit_qty: z.number().positive().optional(),
  })
  .openapi("ProductPatchRequest");

export const ProductDiscontinueRequest = z
  .object({ force: z.boolean().optional() })
  .openapi("ProductDiscontinueRequest");

export const ProductListResponse = z
  .object({
    items: z.array(Product),
    next_cursor: Uuid.nullable(),
  })
  .openapi("ProductListResponse");

export const QrPrintRequest = z
  .object({ product_ids: z.array(Uuid).min(1).max(500) })
  .openapi("QrPrintRequest");

// --- product-specific UoM conversions (spec 01 §2.5) ---

export const ProductConversion = z
  .object({
    id: Uuid,
    product_id: Uuid,
    from_uom_id: Uuid,
    to_uom_id: Uuid,
    factor: z.number().positive(),
  })
  .openapi("ProductConversion");

export const ProductConversionCreateRequest = z
  .object({
    from_uom_id: Uuid,
    to_uom_id: Uuid,
    factor: z.number().positive(),
  })
  .openapi("ProductConversionCreateRequest");

export const ProductConversionListResponse = z
  .object({ items: z.array(ProductConversion) })
  .openapi("ProductConversionListResponse");

// --- where-used (spec 01 §5) ---

export const WhereUsedRef = z
  .object({
    bom_id: Uuid,
    product_id: Uuid,
    product_sku: z.string(),
    version: z.number().int(),
    status: z.string(),
  })
  .openapi("WhereUsedRef");

export const WhereUsedResponse = z
  .object({
    as_component: z.array(WhereUsedRef),
    as_output: z.array(WhereUsedRef),
  })
  .openapi("WhereUsedResponse");

// --- BOMs (spec 01 §2.6–2.9) ---

export const BomStatus = z.enum(["draft", "active", "superseded", "archived"]);
export const BomOutputKind = z.enum(["co_product", "by_product"]);

export const BomLine = z
  .object({
    id: Uuid,
    line_no: z.number().int(),
    component_product_id: Uuid,
    qty: z.number().positive(),
    uom_id: Uuid,
    scrap_pct: z.number().min(0).lt(100),
    consume_at_step_no: z.number().int().nullable(),
    is_optional: z.boolean(),
    notes: z.string().nullable(),
    substitutes: z.array(
      z.object({
        id: Uuid,
        substitute_product_id: Uuid,
        priority: z.number().int(),
        qty_factor: z.number().positive(),
      }),
    ),
  })
  .openapi("BomLine");

export const BomLineInput = z
  .object({
    line_no: z.number().int(),
    component_product_id: Uuid,
    qty: z.number().positive(),
    uom_id: Uuid,
    scrap_pct: z.number().min(0).lt(100).optional(),
    consume_at_step_no: z.number().int().nullable().optional(),
    is_optional: z.boolean().optional(),
    notes: z.string().max(2000).nullable().optional(),
    substitutes: z
      .array(
        z.object({
          substitute_product_id: Uuid,
          priority: z.number().int().optional(),
          qty_factor: z.number().positive().optional(),
        }),
      )
      .optional(),
  })
  .openapi("BomLineInput");

/**
 * A co-/by-product output (spec 01 §2.9). `nrv_per_price_unit_minor` is a cost
 * signal, so it is `.optional()` and redacted without catalogue.cost.read (§7).
 */
export const BomOutput = z
  .object({
    id: Uuid,
    output_kind: BomOutputKind,
    product_id: Uuid,
    qty: z.number().positive(),
    uom_id: Uuid,
    apportionment_pct: z.number().positive().lt(100).nullable(),
    nrv_per_price_unit_minor: MinorMoney.nullable().optional(),
    nrv_price_unit_qty: z.number().positive().nullable(),
    currency: Currency.nullable(),
  })
  .openapi("BomOutput");

export const BomOutputInput = z
  .object({
    output_kind: BomOutputKind,
    product_id: Uuid,
    qty: z.number().positive(),
    uom_id: Uuid,
    apportionment_pct: z.number().positive().lt(100).nullable().optional(),
    nrv_per_price_unit_minor: MinorMoney.min(0).nullable().optional(),
    nrv_price_unit_qty: z.number().positive().nullable().optional(),
    currency: Currency.nullable().optional(),
  })
  // NRV is a money value, so a currency must accompany it (spec §2.9 "currency
  // required with NRV"; mirrors the DB bom_outputs_by_product_currency CHECK and
  // the by_product ⇒ NRV shape).
  .superRefine((val, ctx) => {
    if (
      val.nrv_per_price_unit_minor !== null &&
      val.nrv_per_price_unit_minor !== undefined &&
      (val.currency === null || val.currency === undefined)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["currency"],
        message: "currency is required when nrv_per_price_unit_minor is present",
      });
    }
  })
  .openapi("BomOutputInput");

export const Bom = z
  .object({
    id: Uuid,
    product_id: Uuid,
    facility_id: Uuid.nullable(),
    version: z.number().int(),
    name: z.string().nullable(),
    is_phantom: z.boolean(),
    output_qty: z.number().positive(),
    output_uom_id: Uuid,
    status: BomStatus,
    effective_from: BusinessDate.nullable(),
    effective_to: BusinessDate.nullable(),
    activation_note: z.string().nullable(),
    activated_by: Uuid.nullable(),
    activated_at: isoDateTime().nullable(),
    lines: z.array(BomLine),
    outputs: z.array(BomOutput),
    created_by: Uuid,
    updated_by: Uuid,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("Bom");

export const BomSummary = z
  .object({
    id: Uuid,
    product_id: Uuid,
    facility_id: Uuid.nullable(),
    version: z.number().int(),
    name: z.string().nullable(),
    is_phantom: z.boolean(),
    status: BomStatus,
    effective_from: BusinessDate.nullable(),
    effective_to: BusinessDate.nullable(),
  })
  .openapi("BomSummary");

export const BomListResponse = z.object({ items: z.array(BomSummary) }).openapi("BomListResponse");

export const BomCreateRequest = z
  .object({
    facility_id: Uuid.nullable().optional(),
    copy_from_version_id: Uuid.optional(),
    template_code: z.string().min(1).max(64).optional(),
    output_qty: z.number().positive().optional(),
    output_uom_id: Uuid.optional(),
    is_phantom: z.boolean().optional(),
    name: z.string().max(200).nullable().optional(),
  })
  .openapi("BomCreateRequest");

export const BomPatchRequest = z
  .object({
    name: z.string().max(200).nullable().optional(),
    output_qty: z.number().positive().optional(),
    output_uom_id: Uuid.optional(),
    is_phantom: z.boolean().optional(),
  })
  .openapi("BomPatchRequest");

export const BomLinesReplaceRequest = z
  .object({ lines: z.array(BomLineInput) })
  .openapi("BomLinesReplaceRequest");

export const BomOutputsReplaceRequest = z
  .object({ outputs: z.array(BomOutputInput) })
  .openapi("BomOutputsReplaceRequest");

export const VersionActivateRequest = z
  .object({
    effective_from: BusinessDate,
    activation_note: z.string().min(1).max(2000),
  })
  .openapi("VersionActivateRequest");

/**
 * Withdraw a scheduled activation (spec 01 §3.3.5, D-261) — the exact inverse
 * of `VersionActivateRequest`, and the only way an activation is ever undone.
 *
 * It carries NO date, deliberately. Withdraw is legal only while the version is
 * not yet in force (`effective_from > tenant-local today`), so there is nothing
 * for the caller to choose: the row is removed from the future, and the
 * predecessor already recorded in `catalogue_change_log` comes back. Naming an
 * earlier date instead would leave the mistaken row still scheduled and need a
 * second concept to clear it, which D-261 rejected.
 *
 * `reason` mirrors `activation_note`'s bounds exactly (min 1, max 2000) because
 * it plays the same part: it is the "light ECO" note copied onto the change-log
 * row this withdrawal writes, and the two ends of one correction should not be
 * bounded differently.
 */
export const VersionWithdrawRequest = z
  .object({
    reason: z.string().min(1).max(2000),
  })
  .openapi("VersionWithdrawRequest");

export const BomActivateResponse = z
  .object({
    bom: Bom,
    superseded_version_id: Uuid.nullable(),
  })
  .openapi("BomActivateResponse");

/**
 * spec 01 §3.3.5, D-261. Shaped exactly like `BomActivateResponse` with the
 * predecessor field inverted: activation reports what it SUPERSEDED, withdrawal
 * reports what it RESTORED. `restored_version_id` is declared `.nullable()`
 * rather than `.optional()` to mirror `superseded_version_id` above — the two
 * responses are rendered by the same screen, and a field that is present on one
 * and absent on the other reads as a bug in the client.
 *
 * `null` means the withdrawn version superseded nothing, so the product is left
 * with NO active version. Per §3.3.5 that is a legal state, not an error (§3.4
 * then answers 404 `NO_EFFECTIVE_BOM`): the caller must render it as a fact the
 * planner now has to act on, never retry it as a failure.
 */
export const BomWithdrawResponse = z
  .object({
    bom: Bom,
    restored_version_id: Uuid.nullable(),
  })
  .openapi("BomWithdrawResponse");

/**
 * One yield-division factor applied to a line's requirement (spec 01 §3.3.7,
 * §3.7, M01-S05). `divisor = 1 − scrap_pct/100`; the net qty divides by each in
 * turn (never subtracts) so component + operation + assembly scrap compound.
 */
export const BomScrapFactor = z
  .object({
    kind: z.enum(["component", "operation", "assembly"]),
    scrap_pct: z.number(),
    divisor: z.number(),
  })
  .openapi("BomScrapFactor");

/**
 * The per-line scrap math for an explosion node (spec 01 §5, M01-S05): the gross
 * requirement into this node (`base_qty`, carrying every ancestor's yield via the
 * multiplier), the ordered division factors applied AT this node, and the
 * resulting `qty_net_of_scrap` — so `1860 ÷ 0.97 ÷ 0.98 ÷ 0.99` is fully shown,
 * traceable to 6 decimals (SAP PP parity).
 */
export const BomExplosionMath = z
  .object({
    base_qty: z.number(),
    factors: z.array(BomScrapFactor),
    qty_net_of_scrap: z.number(),
  })
  .openapi("BomExplosionMath");

/**
 * Exploded BOM node (spec 01 §5 GET /boms/:id?explode). Flat, depth-first order
 * with `level` and an ancestor `path` of component product ids — avoids a
 * recursive schema while carrying the full tree (phantoms inlined, net-of-scrap
 * quantities per 1 output unit). `math` shows the component/operation/assembly
 * scrap divisions that produced `qty_net_of_scrap` (§3.3.7, M01-S05).
 */
export const BomExplosionNode = z
  .object({
    component_product_id: Uuid,
    sku: z.string(),
    qty_net_of_scrap: z.number(),
    uom_id: Uuid,
    is_phantom: z.boolean(),
    level: z.number().int(),
    path: z.array(Uuid),
    math: BomExplosionMath,
  })
  .openapi("BomExplosionNode");

export const BomExplosionResponse = z
  .object({
    bom_id: Uuid,
    product_id: Uuid,
    output_qty: z.number(),
    nodes: z.array(BomExplosionNode),
  })
  .openapi("BomExplosionResponse");

// --- routings (spec 01 §2.10, §2.11) ---

export const RoutingStatus = z.enum(["draft", "active", "superseded", "archived"]);
export const RunBasis = z.enum(["per_unit", "per_batch"]);

export const RoutingStep = z
  .object({
    id: Uuid,
    step_no: z.number().int(),
    name: z.string(),
    work_center_id: Uuid,
    alternate_work_center_ids: z.array(Uuid).nullable(),
    setup_minutes: z.number().min(0),
    run_basis: RunBasis,
    run_minutes: z.number().min(0),
    batch_qty: z.number().positive().nullable(),
    setup_key: z.string().nullable(),
    operation_scrap_pct: z.number().min(0).lt(100),
    qc_control_point_id: Uuid.nullable(),
    requires_measured_capture: z.boolean(),
    requires_serial_capture: z.boolean(),
    /** Spec 04 §2.2 / §2.13 (added 2026-08-07, slice 04-C-4 — D-087 ruling 6).
     * A step whose quantity reports earn PIECE RATE. It lives here rather than
     * on the work order because it is a property of the OPERATION a factory
     * pays by the metre for, and it is snapshotted onto
     * `work_order_operations.piece_rate_eligible` at release like every other
     * routing fact (§9.15: a live WO keeps its snapshot). Migration 0019
     * created that column with no writer anywhere; this is the writer. */
    piece_rate_eligible: z.boolean(),
    backflush: z.boolean(),
    is_outsourced: z.boolean(),
    outsource_code: z.string().nullable(),
    min_crew: z.number().int().min(1),
    can_overlap: z.boolean(),
    instructions: z.record(z.string(), z.unknown()),
  })
  .openapi("RoutingStep");

export const RoutingStepInput = z
  .object({
    step_no: z.number().int(),
    name: z.string().min(1).max(200),
    work_center_id: Uuid,
    alternate_work_center_ids: z.array(Uuid).nullable().optional(),
    setup_minutes: z.number().min(0).optional(),
    run_basis: RunBasis.optional(),
    run_minutes: z.number().min(0),
    batch_qty: z.number().positive().nullable().optional(),
    setup_key: z.string().max(64).nullable().optional(),
    operation_scrap_pct: z.number().min(0).lt(100).optional(),
    qc_control_point_id: Uuid.nullable().optional(),
    requires_measured_capture: z.boolean().optional(),
    requires_serial_capture: z.boolean().optional(),
    piece_rate_eligible: z.boolean().optional(),
    backflush: z.boolean().optional(),
    is_outsourced: z.boolean().optional(),
    outsource_code: z.string().max(64).nullable().optional(),
    min_crew: z.number().int().min(1).optional(),
    can_overlap: z.boolean().optional(),
    instructions: z.record(z.string(), z.unknown()).optional(),
  })
  .openapi("RoutingStepInput");

export const Routing = z
  .object({
    id: Uuid,
    product_id: Uuid,
    facility_id: Uuid,
    version: z.number().int(),
    name: z.string().nullable(),
    status: RoutingStatus,
    effective_from: BusinessDate.nullable(),
    effective_to: BusinessDate.nullable(),
    activation_note: z.string().nullable(),
    activated_by: Uuid.nullable(),
    activated_at: isoDateTime().nullable(),
    steps: z.array(RoutingStep),
    created_by: Uuid,
    updated_by: Uuid,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("Routing");

export const RoutingSummary = z
  .object({
    id: Uuid,
    product_id: Uuid,
    facility_id: Uuid,
    version: z.number().int(),
    name: z.string().nullable(),
    status: RoutingStatus,
    effective_from: BusinessDate.nullable(),
    effective_to: BusinessDate.nullable(),
  })
  .openapi("RoutingSummary");

export const RoutingListResponse = z
  .object({ items: z.array(RoutingSummary) })
  .openapi("RoutingListResponse");

export const RoutingCreateRequest = z
  .object({
    facility_id: Uuid,
    copy_from_version_id: Uuid.optional(),
    template_code: z.string().min(1).max(64).optional(),
    name: z.string().max(200).nullable().optional(),
  })
  .openapi("RoutingCreateRequest");

export const RoutingPatchRequest = z
  .object({ name: z.string().max(200).nullable().optional() })
  .openapi("RoutingPatchRequest");

export const RoutingStepsReplaceRequest = z
  .object({ steps: z.array(RoutingStepInput) })
  .openapi("RoutingStepsReplaceRequest");

export const RoutingActivateResponse = z
  .object({
    routing: Routing,
    superseded_version_id: Uuid.nullable(),
  })
  .openapi("RoutingActivateResponse");

/**
 * The routing half of `BomWithdrawResponse` (spec 01 §3.3.5, D-261) — same
 * shape, same reading of `null`: nothing was restored, the product is left with
 * no active routing, and §3.4 answers 404 `NO_EFFECTIVE_ROUTING`.
 */
export const RoutingWithdrawResponse = z
  .object({
    routing: Routing,
    restored_version_id: Uuid.nullable(),
  })
  .openapi("RoutingWithdrawResponse");

// --- routing step bindings (spec 01 §2.18) ---

export const RoutingStepBinding = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    step_template_ref: z.string(),
    work_center_id: Uuid,
  })
  .openapi("RoutingStepBinding");

export const RoutingStepBindingsPutRequest = z
  .object({
    facility_id: Uuid,
    bindings: z.array(
      z.object({ step_template_ref: z.string().min(1).max(128), work_center_id: Uuid }),
    ),
  })
  .openapi("RoutingStepBindingsPutRequest");

export const RoutingStepBindingListResponse = z
  .object({ items: z.array(RoutingStepBinding) })
  .openapi("RoutingStepBindingListResponse");

// --- standard cost runs (spec 01 §2.15, §2.16) ---

export const CostRunScope = z.enum(["all_make_products", "selected_products"]);
export const CostRunStatus = z.enum([
  "draft",
  "computing",
  "computed",
  "compute_failed",
  "released",
  "discarded",
]);

/**
 * Why a roll-up stopped (spec 01 §9.4/§9.5). A CODE, not a sentence: the run
 * persists the fact and the client renders it in the reader's language
 * (D-225 ruling 2) — before that ruling this arrived as English prose in
 * `note`, overwriting whatever the creator had typed there.
 */
export const CostRunFailureCode = z.enum([
  "APPORTIONMENT_SUM_INVALID",
  "BOM_CYCLE_DETECTED",
  "BOM_DEPTH_EXCEEDED",
]);

export const CostRun = z
  .object({
    id: Uuid,
    facility_id: Uuid.nullable(),
    scope: CostRunScope,
    product_ids: z.array(Uuid).nullable(),
    as_of_date: BusinessDate,
    status: CostRunStatus,
    currency: Currency,
    /** The creator's OWN words, verbatim. A service never writes here (D-225). */
    note: z.string().nullable(),
    failure_code: CostRunFailureCode.nullable(),
    /** The product whose roll-up stopped — always set when `failure_code` is. */
    failure_product_id: Uuid.nullable(),
    /** The effective BOM version that broke the invariant. Null for
     * `BOM_DEPTH_EXCEEDED`, which is a property of the path, not of a version. */
    failure_bom_id: Uuid.nullable(),
    released_by: Uuid.nullable(),
    released_at: isoDateTime().nullable(),
    created_by: Uuid,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("CostRun");

export const CostRunCreateRequest = z
  .object({
    as_of_date: BusinessDate,
    scope: CostRunScope,
    facility_id: Uuid.nullable().optional(),
    product_ids: z.array(Uuid).optional(),
    note: z.string().max(2000).optional(),
  })
  // scope ⇔ product_ids pairing, mirroring the DB standard_cost_runs_scope_products
  // CHECK (spec §2.15): selected_products needs a non-empty product_ids;
  // all_make_products must not carry a selection (persisted as NULL).
  .superRefine((val, ctx) => {
    if (val.scope === "selected_products") {
      if (val.product_ids === undefined || val.product_ids.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["product_ids"],
          message: "product_ids is required (non-empty) when scope is selected_products",
        });
      }
    } else if (val.product_ids !== undefined && val.product_ids.length > 0) {
      ctx.addIssue({
        code: "custom",
        path: ["product_ids"],
        message: "product_ids must be omitted when scope is all_make_products",
      });
    }
  })
  .openapi("CostRunCreateRequest");

export const CostRunListResponse = z
  .object({ items: z.array(CostRun), next_cursor: Uuid.nullable() })
  .openapi("CostRunListResponse");

/**
 * A per-product roll-up result (spec 01 §2.16). Every `*_minor` column and
 * `breakdown` are redacted (omitted) without catalogue.cost.read (§7) — hence
 * `.optional()`. The whole cost-run route family is itself cost.read-gated, so
 * these normally serialize; the redaction covers any future non-cost embedding.
 */
export const CostItem = z
  .object({
    id: Uuid,
    cost_run_id: Uuid,
    product_id: Uuid,
    bom_id: Uuid.nullable(),
    routing_id: Uuid.nullable(),
    price_unit_qty: z.number().positive(),
    level: z.number().int(),
    apportionment_pct_applied: z.number().nullable(),
    material_cost_minor: MinorMoney.optional(),
    labor_cost_minor: MinorMoney.optional(),
    overhead_cost_minor: MinorMoney.optional(),
    outsourcing_cost_minor: MinorMoney.optional(),
    by_product_credit_minor: MinorMoney.optional(),
    total_cost_minor: MinorMoney.optional(),
    currency: Currency,
    breakdown: z.record(z.string(), z.unknown()).optional(),
  })
  .openapi("CostItem");

export const CostItemListResponse = z
  .object({ items: z.array(CostItem), next_cursor: Uuid.nullable() })
  .openapi("CostItemListResponse");

// --- templates (spec 01 §2.17) ---

export const TemplateKind = z.enum(["product", "bom", "routing"]);

export const CatalogueTemplate = z
  .object({
    id: Uuid,
    kind: TemplateKind,
    code: z.string(),
    name: z.string(),
    payload: z.record(z.string(), z.unknown()),
    source: UomSource,
    status: ActiveStatus,
  })
  .openapi("CatalogueTemplate");

export const TemplateCreateRequest = z
  .object({
    kind: TemplateKind,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    payload: z.record(z.string(), z.unknown()).optional(),
    from_entity: z.object({ kind: TemplateKind, id: Uuid }).optional(),
  })
  .openapi("TemplateCreateRequest");

export const TemplateListResponse = z
  .object({ items: z.array(CatalogueTemplate) })
  .openapi("TemplateListResponse");

// --- change log (spec 01 §2.12) ---

export const ChangeLogEntry = z
  .object({
    id: Uuid,
    entity_type: z.enum(["bom", "routing"]),
    product_id: Uuid,
    from_version_id: Uuid.nullable(),
    to_version_id: Uuid,
    effective_from: BusinessDate,
    reason: z.string(),
    actor_id: Uuid,
    created_at: isoDateTime(),
  })
  .openapi("ChangeLogEntry");

export const ChangeLogListResponse = z
  .object({ items: z.array(ChangeLogEntry), next_cursor: Uuid.nullable() })
  .openapi("ChangeLogListResponse");

// --- floor delta cache (spec 01 §5 GET /floor-cache, S9) ---

export const FloorCacheResponse = z
  .object({
    products: z.array(Product),
    routings: z.array(Routing),
    deleted: z.object({
      product_ids: z.array(Uuid),
      routing_ids: z.array(Uuid),
    }),
    next_since: z.string(),
  })
  .openapi("FloorCacheResponse");

/**
 * Problem codes the module-01 catalogue surface raises (UPPER_SNAKE, D-022).
 * Mirrors the spec §5 canonical set VERBATIM — every top-level `code` the
 * catalogue services return, grouped products → UoM → attributes → BOM → routing
 * → version lifecycle (shared by both version kinds) → template → cursor → cost.
 * `COST_RELEASE_ROLE_FORBIDDEN` is enforced by the Module-16 role editor (spec
 * §13.5), listed here as a catalogue-domain code.
 * Field-error codes (inside `SCHEMA_VALIDATION_FAILED`) and import-row cell codes
 * are NOT top-level problem codes and are intentionally excluded.
 */
export const catalogueProblemCodes = [
  "PRODUCT_NOT_FOUND",
  "PRODUCT_STATE_INVALID",
  "SKU_TAKEN",
  "SKU_IMMUTABLE",
  "UOM_NOT_FOUND",
  "UOM_CODE_TAKEN",
  "UOM_IN_USE",
  "UOM_NOT_CONVERTIBLE",
  "UOM_DIMENSION_MISMATCH",
  "UOM_CONVERSION_EXISTS",
  "UOM_CONVERSION_NOT_FOUND",
  "ATTRIBUTE_SCHEMA_NOT_FOUND",
  "ATTRIBUTE_SCHEMA_EXISTS",
  "SCHEMA_VALIDATION_FAILED",
  "BOM_NOT_FOUND",
  "BOM_NO_LINES",
  "BOM_NOT_ARCHIVABLE",
  "BOM_LINE_INVALID",
  "BOM_OUTPUT_INVALID",
  "BOM_CYCLE_DETECTED",
  "BOM_DEPTH_EXCEEDED",
  "PHANTOM_NO_OUTPUTS",
  "PHANTOM_KIND_INVALID",
  "APPORTIONMENT_SUM_INVALID",
  "NO_EFFECTIVE_BOM",
  "PRODUCT_IN_ACTIVE_BOM",
  "ROUTING_NOT_FOUND",
  "ROUTING_NO_STEPS",
  "ROUTING_NOT_ARCHIVABLE",
  "ROUTING_STEP_INVALID",
  "ROUTING_STEP_UNBOUND",
  "WORK_CENTER_NOT_IN_FACILITY",
  "NO_EFFECTIVE_ROUTING",
  "EFFECTIVE_DATE_PAST",
  "VERSION_CONFLICT",
  // The withdraw refusals (spec 01 §3.3.5, §5, D-261) — shared by the BOM and
  // routing halves, like the two codes above, because withdraw is one rule
  // stated once and applied to both version kinds.
  "VERSION_IN_FORCE",
  "VERSION_NOT_ACTIVE",
  "PREDECESSOR_NOT_RESTORABLE",
  // Housekeeping refusal (spec 01 §3.3.5 Archive, D-261 amended 2026-09-08):
  // `superseded → archived` while the same-scope active version is not yet in
  // force — that superseded version is what a withdrawal would bring back.
  "VERSION_STILL_RESUMABLE",
  "TEMPLATE_NOT_FOUND",
  "TEMPLATE_CODE_TAKEN",
  "TEMPLATE_KIND_MISMATCH",
  "TEMPLATE_SOURCE_NOT_FOUND",
  "INVALID_CURSOR",
  "COST_RUN_NOT_FOUND",
  "COST_RUN_NOT_RELEASABLE",
  "COST_RUN_SCOPE_INVALID",
  "COST_RELEASE_ROLE_FORBIDDEN",
] as const;

export type CatalogueProblemCode = (typeof catalogueProblemCodes)[number];
